import {Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {FormBuilder, FormGroup, NgForm, Validators} from '@angular/forms';
import {MatDialog} from '@angular/material/dialog';
import {Process, ProcessService} from '../../../../lib/process/process.service';
import {ProcessCreateModel, ProcessTaskRecordConfigNameTemplate} from '../process-create.model';
import {FieldValidationError, ForwardingNgFormRef, LocalFormGroupValidationErrors} from '../../../../lib/util/services';
import {AppValidators} from '../../../../util/app-validators';
import {ConfigurationService} from '../../../../lib/configuration/configuration.service';
import {NgbDatePickerParserFormatter} from '../../../../util/ngb-datepicker';
import {WorkflowMultiselectProvider} from '../../../../lib/workflow/workflow-multiselect.provider';
import {UiConstants} from '../../../../util/core-utils';
import {Angular2Multiselects} from '../../../../util/multiselect';
import {
  TaskRecordCreateDialogData,
  TaskRecordCreateDialogMode,
  TaskRecordCreateDialogResult,
  TaskRecordCreateFormComponent
} from '../../../task/task-record/dialogs/task-record-create-material-dialog/task-record-create-form/task-record-create-form.component';
import ProcessFieldType = Process.ProcessFieldType;

@Component({
  selector: 'app-process-base-form',
  templateUrl: './process-base-form.component.html'
})
export class ProcessBaseFormComponent implements OnInit {
  Process = Process;
  ProcessTaskRecordConfigNameTemplate = ProcessTaskRecordConfigNameTemplate;
  ProcessFieldType = Process.ProcessFieldType;

  @Input() data!: ProcessBaseDialogData;
  @Output() saving = new EventEmitter<boolean>();
  @Output() finished = new EventEmitter<ProcessBaseDialogResult>();

  model!: ProcessCreateModel;
  dropdownSettings!: Angular2Multiselects.Settings;

  @ViewChild('f', {static: true}) form!: NgForm;
  formGroup!: FormGroup;

  private formGroupValidationErrors!: LocalFormGroupValidationErrors;
  private fieldErrors: FieldValidationError<Process.ValidatedField> = FieldValidationError.empty<Process.ValidatedField>();

  @ViewChild('taskRecordForm') taskRecordFormComponent?: TaskRecordCreateFormComponent;
  taskRecordFormData?: TaskRecordCreateDialogData;

  get hasSelectedWorkflow() {
    return !!this.model?.selectedWorkflow;
  }

  constructor(
    private fb: FormBuilder,
    private workflowMultiselectProvider: WorkflowMultiselectProvider,
    private configurationService: ConfigurationService,
    private processService: ProcessService,
    private datePickerParserFormatter: NgbDatePickerParserFormatter,
    private dialog: MatDialog
  ) {
  }

  ngOnInit() {
    this.model = new ProcessCreateModel(this.datePickerParserFormatter, this.configurationService.getConfiguration(), this.data.process);
    this.formGroup = this.createFormGroup(this.fb);
    this.formGroupValidationErrors = LocalFormGroupValidationErrors.ofForm(
      this.createForwardingHtmlForm(),
      this.formGroup
    );
    this.initDropdownSettings();
    if (!this.readonly) {
      this.initSearch();
    }
    this.loadModelData();
  }

  initDropdownSettings() {
    this.dropdownSettings = new Angular2Multiselects.SettingsBuilder()
      .singleSelection(true)
      .enableSearchFilter(true)
      .enableCheckAll(false)
      .remoteSearch(true)
      .translate(true)
      .build();
  }

  initSearch() {
    if (this.create) {
      this.onWorkflowSearch();
    }
  }

  onWorkflowSearch(predicate?: string) {
    this.workflowMultiselectProvider.loadActive(predicate).subscribe(items => {
      this.model.workflowList = items;
    });
  }

  loadModelData() {
  }

  updateTaskRecordFormData() {
    this.taskRecordFormData = undefined;
    setTimeout(() => {
      if (this.create && this.model.selectedWorkflow && this.model.selectedWorkflow.firstTaskId) {
        this.taskRecordFormData = {
          taskId: this.model.selectedWorkflow.firstTaskId,
          mode: TaskRecordCreateDialogMode.REQUEST_ONLY,
          nameOptional: this.model.taskRecordNameTemplate.length > 0
        };
      }
    });
  }

  submit() {
    this.formGroup.markAllAsTouched();
    this.form.onSubmit(undefined as any);

    if (this.hasLocalFieldError() || this.fieldErrors.hasError()) {
      return;
    }
    if (this.data.process) {
      this.save();
    } else {
      // Create mode: Trigger the Task Record form's save method
      if (this.taskRecordFormComponent && this.taskRecordFormData) {
        // This will validate the embedded task form.
        // If successful, it emits `finished` and triggers `onTaskRecordFormFinished`
        this.taskRecordFormComponent.createTaskRecord();
      }
    }
  }

  onTaskRecordFormFinished(result: TaskRecordCreateDialogResult) {
    if (result && result.success && result.request) {
      this.model.taskRecordCreateRequest = result.request;
      this.save();
    } else {
      this.model.taskRecordCreateRequest = undefined;
    }
  }

  save() {
    this.saving.emit(true);
    if (!this.data.process) {
      this.processService.create(this.model.toCreateRequest()).subscribe((value) => {
        this.saving.emit(false);
        this.finished.emit(value);
      }, (error: any) => {
        this.saving.emit(false);
        if (error instanceof FieldValidationError) {
          this.fieldErrors = error.withForm(this.form);
        } else {
          this.finished.emit();
        }
      });
    } else if (!this.readonly) {
      this.processService.update(this.model.toUpdateRequest()).subscribe((value) => {
        this.saving.emit(false);
        this.finished.emit({id: this.model.id});
      }, (error: any) => {
        this.saving.emit(false);
        if (error instanceof FieldValidationError) {
          this.fieldErrors = error.withForm(this.form);
        } else {
          this.finished.emit();
        }
      });
    } else {
      this.saving.emit(false);
      this.finished.emit();
    }
  }

  hasFieldError(field: Process.ValidatedField): boolean {
    return this.fieldErrors.hasError(field);
  }

  removeFieldError(field: Process.ValidatedField) {
    this.fieldErrors = this.fieldErrors.removeError(field);
  }

  getFieldErrorText(field: Process.ValidatedField): string {
    return this.fieldErrors.getErrorText(field);
  }

  private createForwardingHtmlForm() {
    return new ForwardingNgFormRef({formFn: () => this.form});
  }

  private createFormGroup(fb: FormBuilder): FormGroup {
    return fb.group({
      name: fb.control({
        value: this.model.name,
        disabled: this.readonly
      }, [AppValidators.tempValidator({
        validator: Validators.required,
        disabled: () => !this.isManagedField(ProcessFieldType.NAME) || this.readonly || !this.isRequiredField(ProcessFieldType.NAME)
      })]),
      workflow: fb.control({
        value: this.model.workflow,
        disabled: !this.create
      }, [AppValidators.tempValidator({validator: Validators.required, disabled: () => !this.create})]),
      externalId: fb.control({
        value: this.model.externalId,
        disabled: this.readonly
      }, [AppValidators.tempValidator({
        validator: Validators.required,
        disabled: () => !this.isManagedField(ProcessFieldType.EXTERNAL_ID) || !(this.edit || (!this.readonly && this.isRequiredField(ProcessFieldType.EXTERNAL_ID)))
      })]),
      description: fb.control({
        value: this.model.description,
        disabled: this.readonly
      }, [AppValidators.tempValidator({
        validator: Validators.required,
        disabled: () => !this.isManagedField(ProcessFieldType.DESCRIPTION) || this.readonly || !this.isRequiredField(ProcessFieldType.DESCRIPTION)
      })]),
      deadlineDate: fb.control({
        value: this.model.deadlineDate,
        disabled: this.readonly
      }, [AppValidators.validateLocalDate, AppValidators.tempValidator({
        validator: Validators.required,
        disabled: () => !this.isManagedField(ProcessFieldType.DEADLINE) || this.readonly || !this.isRequiredField(ProcessFieldType.DEADLINE)
      })]),
      namePattern: fb.control({value: this.model.taskRecordNameTemplate, disabled: this.readonly}, [])
    });
  }

  hasLocalFieldError(formControlName?: string, errorCode?: string): boolean {
    return this.formGroupValidationErrors.hasFieldError(formControlName, errorCode);
  }

  getTextMaximumLength(): number {
    return UiConstants.maximumVarcharLength;
  }

  getNumberMaximumLength(): string {
    return UiConstants.maxInputNumberLength;
  }

  get readonly(): boolean {
    return this.data.readonly;
  }

  get create(): boolean {
    return !this.readonly && !this.edit;
  }

  get edit(): boolean {
    return !this.readonly && this.model.process !== undefined;
  }

  get showAdditionalFields(): boolean {
    return this.model.process !== undefined;
  }

  isRequiredField(field: Process.ProcessFieldType): boolean {
    return !!this.model.selectedWorkflow && this.model.selectedWorkflow.requiredFields.contains(field);
  }

  isManagedField(field: Process.ProcessFieldType): boolean {
    return !!this.model.selectedWorkflow && this.model.selectedWorkflow.managedFields.contains(field);
  }
}

export class ProcessBaseDialogData {
  readonly: boolean;
  process?: Process.Process;
}

export interface ProcessBaseDialogResult {
  id?: number;
}
